/**
 * EntityDetailPage - Dettaglio singola entità (Turbina/FAP/Iniettore)
 *
 * Features:
 * - Visualizzazione dati completi entità
 * - Cambio stato workflow (accettazione → lavorazione → completata → consegnata)
 * - Storico lavorazioni e note
 * - Galleria foto (integrazione CORE PhotoCapture)
 * - Documenti allegati
 * - Chat contestuale
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  FileText,
  User,
  Truck,
  Settings,
  AlertCircle,
  Calendar,
  Hash,
  Package
} from 'lucide-react';
import { useAuth } from '../../../components/auth/AuthProvider';
import TurbinePhotoGallery from '../components/TurbinePhotoGallery';
import { appToast } from '../../../lib/appDialog';

type EntityStatus = 'in_accettazione' | 'in_lavorazione' | 'completata' | 'consegnata' | 'annullata';

interface EntityDetail {
  id: string;
  numero_progressivo: number;
  qr_code?: string;
  marca_turbina?: string;
  modello_turbina?: string;
  matricola?: string;
  stato: EntityStatus;
  data_ingresso: string;
  data_uscita?: string;
  cliente_id?: number;
  cliente_nome?: string;
  cliente_telefono?: string;
  mezzo_id?: number;
  mezzo_targa?: string;
  mezzo_telaio?: string;
  note?: string;
  difetti_riscontrati?: string;
  lavorazioni_effettuate?: string;
  costo_preventivo?: number;
  costo_finale?: number;
}

const STATUS_LABELS: Record<EntityStatus, string> = {
  in_accettazione: '📥 In Accettazione',
  in_lavorazione: '🔧 In Lavorazione',
  completata: '✅ Completata',
  consegnata: '📦 Consegnata',
  annullata: '❌ Annullata'
};

const STATUS_COLORS: Record<EntityStatus, string> = {
  in_accettazione: 'bg-blue-900/30 text-blue-300 border-blue-700',
  in_lavorazione: 'bg-yellow-900/30 text-yellow-300 border-yellow-700',
  completata: 'bg-green-900/30 text-green-300 border-green-700',
  consegnata: 'bg-gray-700/30 text-gray-300 border-gray-600',
  annullata: 'bg-red-900/30 text-red-300 border-red-700'
};

const STATUS_WORKFLOW: EntityStatus[] = [
  'in_accettazione',
  'in_lavorazione',
  'completata',
  'consegnata'
];

function EntityDetailPage() {
  const { entityId } = useParams<{ entityId: string }>();
  const navigate = useNavigate();
  const { getToken, user } = useAuth();

  const [entity, setEntity] = useState<EntityDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Editable fields
  const [note, setNote] = useState('');
  const [difetti, setDifetti] = useState('');
  const [lavorazioni, setLavorazioni] = useState('');

  useEffect(() => {
    if (entityId) {
      fetchEntityDetail();
    }
  }, [entityId]);

  const fetchEntityDetail = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/turbine/${entityId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Errore ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const turbine = data.data || data;

      setEntity(turbine);
      setNote(turbine.note || '');
      setDifetti(turbine.difetti_riscontrati || '');
      setLavorazioni(turbine.lavorazioni_effettuate || '');

    } catch (err: any) {
      console.error('Errore fetch dettaglio:', err);
      setError(err.message || 'Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: EntityStatus) => {
    if (!entity) return;

    setSaving(true);
    try {
      const token = await getToken();

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/turbine/${entityId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ stato: newStatus })
        }
      );

      if (!response.ok) {
        throw new Error('Errore aggiornamento stato');
      }

      setEntity({ ...entity, stato: newStatus });
    } catch (err: any) {
      console.error('Errore cambio stato:', err);
      appToast.error('Errore aggiornamento stato: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!entity) return;

    setSaving(true);
    try {
      const token = await getToken();

      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/turbine/${entityId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            note,
            difetti_riscontrati: difetti,
            lavorazioni_effettuate: lavorazioni
          })
        }
      );

      if (!response.ok) {
        throw new Error('Errore salvataggio note');
      }

      appToast.success('Note salvate con successo!');
    } catch (err: any) {
      console.error('Errore salvataggio note:', err);
      appToast.error('Errore salvataggio: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-300">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (error || !entity) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full border border-gray-700 text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-100 mb-2">Errore</h2>
          <p className="text-gray-400 mb-6">{error || 'Entità non trovata'}</p>
          <button
            onClick={() => navigate('/apps/lavorazione/list')}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Torna alla lista
          </button>
        </div>
      </div>
    );
  }

  const currentStatusIndex = STATUS_WORKFLOW.indexOf(entity.stato);

  return (
    <div className="min-h-screen bg-gray-900 p-6 space-y-6">
      {/* Header */}
      <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/apps/lavorazione/list')}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors text-gray-300"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>

            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-100">
                  Turbina #{entity.numero_progressivo}
                </h1>
                <span className={`text-sm font-medium px-3 py-1 rounded-full border ${STATUS_COLORS[entity.stato]}`}>
                  {STATUS_LABELS[entity.stato]}
                </span>
              </div>
              {entity.qr_code && (
                <p className="text-sm text-gray-400 font-mono">
                  QR: {entity.qr_code}
                </p>
              )}
            </div>
          </div>

          <div className="text-5xl">⚙️</div>
        </div>
      </div>

      {/* Workflow Status */}
      <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
        <h2 className="text-lg font-semibold text-gray-100 mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-blue-400" />
          Workflow
        </h2>

        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {STATUS_WORKFLOW.map((status, index) => {
            const isActive = status === entity.stato;
            const isPast = index < currentStatusIndex;
            // @ts-ignore - Disponibile per uso futuro nello styling
            const _isFuture = index > currentStatusIndex;

            return (
              <div key={status} className="flex items-center gap-2">
                <button
                  onClick={() => handleStatusChange(status)}
                  disabled={saving}
                  className={`px-4 py-2 rounded-lg border-2 transition-all min-w-fit ${
                    isActive
                      ? STATUS_COLORS[status] + ' shadow-lg'
                      : isPast
                      ? 'bg-gray-700 border-gray-600 text-gray-400'
                      : 'bg-gray-800 border-gray-600 text-gray-500 hover:border-gray-500'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {STATUS_LABELS[status]}
                </button>
                {index < STATUS_WORKFLOW.length - 1 && (
                  <div className={`w-8 h-0.5 ${isPast ? 'bg-green-600' : 'bg-gray-700'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dati Tecnici */}
        <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
          <h2 className="text-lg font-semibold text-gray-100 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-400" />
            Dati Tecnici
          </h2>

          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <Hash className="w-4 h-4 text-gray-400" />
              <span className="font-medium text-gray-400">Numero:</span>
              <span className="text-gray-200">#{entity.numero_progressivo}</span>
            </div>

            {entity.marca_turbina && (
              <div className="flex items-center gap-3 text-sm">
                <span className="font-medium text-gray-400">Marca:</span>
                <span className="text-gray-200">{entity.marca_turbina}</span>
              </div>
            )}

            {entity.modello_turbina && (
              <div className="flex items-center gap-3 text-sm">
                <span className="font-medium text-gray-400">Modello:</span>
                <span className="text-gray-200">{entity.modello_turbina}</span>
              </div>
            )}

            {entity.matricola && (
              <div className="flex items-center gap-3 text-sm">
                <span className="font-medium text-gray-400">Matricola:</span>
                <span className="text-gray-200 font-mono">{entity.matricola}</span>
              </div>
            )}

            <div className="flex items-center gap-3 text-sm">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="font-medium text-gray-400">Data ingresso:</span>
              <span className="text-gray-200">
                {new Date(entity.data_ingresso).toLocaleDateString('it-IT')}
              </span>
            </div>

            {entity.data_uscita && (
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="font-medium text-gray-400">Data uscita:</span>
                <span className="text-gray-200">
                  {new Date(entity.data_uscita).toLocaleDateString('it-IT')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Cliente e Mezzo */}
        <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
          <h2 className="text-lg font-semibold text-gray-100 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-400" />
            Cliente e Mezzo
          </h2>

          <div className="space-y-3">
            {entity.cliente_nome && (
              <div className="flex items-center gap-3 text-sm">
                <User className="w-4 h-4 text-gray-400" />
                <span className="font-medium text-gray-400">Cliente:</span>
                <span className="text-gray-200">{entity.cliente_nome}</span>
              </div>
            )}

            {entity.cliente_telefono && (
              <div className="flex items-center gap-3 text-sm">
                <span className="font-medium text-gray-400">Telefono:</span>
                <span className="text-gray-200">{entity.cliente_telefono}</span>
              </div>
            )}

            {entity.mezzo_targa && (
              <div className="flex items-center gap-3 text-sm">
                <Truck className="w-4 h-4 text-gray-400" />
                <span className="font-medium text-gray-400">Targa:</span>
                <span className="text-gray-200 font-mono">{entity.mezzo_targa}</span>
              </div>
            )}

            {entity.mezzo_telaio && (
              <div className="flex items-center gap-3 text-sm">
                <span className="font-medium text-gray-400">Telaio:</span>
                <span className="text-gray-200 font-mono">{entity.mezzo_telaio}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Note e Lavorazioni */}
      <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-100 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-400" />
            Note e Lavorazioni
          </h2>
          <button
            onClick={handleSaveNotes}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Difetti riscontrati
            </label>
            <textarea
              value={difetti}
              onChange={(e) => setDifetti(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-600 text-gray-100 placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Descrivi i difetti riscontrati durante l'accettazione..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Lavorazioni effettuate
            </label>
            <textarea
              value={lavorazioni}
              onChange={(e) => setLavorazioni(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-600 text-gray-100 placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Descrivi le lavorazioni effettuate..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Note generali
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 bg-gray-900 border border-gray-600 text-gray-100 placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Note aggiuntive..."
            />
          </div>
        </div>
      </div>

      {/* Foto e Documenti - Integrato con CORE PhotoCapture */}
      {user && entityId && (
        <TurbinePhotoGallery
          turbineId={entityId}
          userId={user.id}
          readonly={false}
        />
      )}
    </div>
  );
}

export default EntityDetailPage;
